import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useReveal from "@/hooks/useReveal";

const GROUPS = [
  {
    label: "Shipping",
    items: [
      {
        q: "When will my AFRQA™ piece ship?",
        a: "Edition 001 of The Africa Frame™ ships to collectors as production batches complete — first wave begins shortly after preorder close. You'll receive a tracked dispatch email with your edition number on the day of shipment.",
      },
      {
        q: "Do you ship internationally?",
        a: "Yes. AFRQA™ ships worldwide from our Los Angeles studio. Duties and import taxes are billed by the destination customs authority on delivery.",
      },
      {
        q: "What carrier do you use?",
        a: "Tracked, signature-required courier service (DHL Express / FedEx International Priority). Domestic US orders ship UPS 2-Day.",
      },
    ],
  },
  {
    label: "Preorder",
    items: [
      {
        q: "What does preorder mean for Edition 001?",
        a: "The Africa Frame™ — Gold Edition is limited to 300 pieces worldwide. Your preorder reserves a unique edition number (e.g., #117 / 300) and guarantees a frame from this drop. Once 300 are claimed, the edition is permanently closed.",
      },
      {
        q: "How is my edition number assigned?",
        a: "Numbers are reserved sequentially the moment your payment is confirmed. Your number is shown immediately on the confirmation page and embossed on your authenticity card.",
      },
      {
        q: "Can I cancel or change my preorder?",
        a: "Yes — write to Afrqa@afrqa.com within 48 hours of placing your preorder for a full refund. After production batches begin, refunds are reviewed case-by-case.",
      },
    ],
  },
  {
    label: "Product",
    items: [
      {
        q: "What is the frame made of?",
        a: "Hand-polished 18k gold-tone metal alloy with matte black acetate temples. CR-39 anti-glare lenses with 100% UVA / UVB protection. Each piece is finished and inspected individually.",
      },
      {
        q: "How heavy is The Africa Frame™?",
        a: "The hardware was engineered to be intentionally lightweight — designed to disappear on the face without compromising on presence or build quality.",
      },
      {
        q: "Are the lenses prescription-ready?",
        a: "Edition 001 ships with non-prescription tinted lenses. Prescription-ready editions are part of the Phase II roadmap.",
      },
    ],
  },
  {
    label: "Future Systems",
    items: [
      {
        q: "What is Phase II?",
        a: "AFRQA™ Phase II is a new generation of wearable objects — Adaptive Vision, future-forward materials, and an expanded language for the studio. Details release as the manifesto unfolds.",
      },
      {
        q: "When does Phase II launch?",
        a: "Phase II is currently in studio. Sign up for The List in the footer to receive the first invitation when it opens.",
      },
    ],
  },
  {
    label: "Support",
    items: [
      {
        q: "How do I contact AFRQA™?",
        a: "Write to Afrqa@afrqa.com for press, partnerships, preorder support, and wholesale inquiries. Replies within 48 hours, Monday through Friday.",
      },
      {
        q: "Where is the studio?",
        a: "AFRQA™ is based in Los Angeles, California. Studio visits are by invitation only.",
      },
    ],
  },
];

export default function FAQPage() {
  const r1 = useReveal();
  const r2 = useReveal();

  return (
    <div data-testid="faq-page" className="bg-[#0a0a0a] text-white">
      <Header />
      <main className="pt-28 md:pt-36 pb-24">
        <section className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div
            ref={r1.ref}
            className={`reveal ${r1.inView ? "in-view" : ""} max-w-3xl`}
          >
            <span className="font-label text-[#d4af37]">— Help & Answers —</span>
            <h1 className="mt-5 font-display text-[clamp(2.6rem,6vw,5.4rem)] leading-[0.96] tracking-[-0.025em]">
              Frequently{" "}
              <span className="font-editorial italic gold-text-gradient">
                asked.
              </span>
            </h1>
            <p className="mt-8 max-w-xl text-[#cfcfca] text-lg leading-relaxed font-light">
              Everything about shipping, preorders, our craft, and the
              future systems on the horizon. Still curious?{" "}
              <Link to="/contact" className="link-underline text-white">
                Write to the studio.
              </Link>
            </p>
          </div>
        </section>

        <section className="mt-14 md:mt-20 max-w-[1100px] mx-auto px-6 md:px-10 space-y-14 md:space-y-20">
          {GROUPS.map((group, gi) => (
            <div key={group.label} data-testid={`faq-group-${gi}`}>
              <div className="flex items-baseline justify-between border-b border-[#1f1f1f] pb-5">
                <h2 className="font-display text-2xl md:text-3xl">
                  {group.label}
                </h2>
                <span className="font-label text-[#6a6a65]">
                  0{gi + 1} / 0{GROUPS.length}
                </span>
              </div>
              <Accordion
                type="single"
                collapsible
                className="mt-2"
                data-testid={`faq-accordion-${gi}`}
              >
                {group.items.map((item, ii) => (
                  <AccordionItem
                    key={item.q}
                    value={`${gi}-${ii}`}
                    className="border-b border-[#1f1f1f]"
                  >
                    <AccordionTrigger
                      data-testid={`faq-q-${gi}-${ii}`}
                      className="font-display text-left text-lg md:text-xl text-white hover:text-[#d4af37] hover:no-underline py-6"
                    >
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#a3a3a3] text-base font-light leading-relaxed pb-6 pr-6 md:pr-12">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </section>

        <section
          ref={r2.ref}
          className={`reveal ${r2.inView ? "in-view" : ""} mt-28 max-w-[1100px] mx-auto px-6 md:px-10`}
        >
          <div className="border border-[#1f1f1f] bg-[#0c0c0c] p-10 md:p-14 text-center">
            <span className="font-label text-[#d4af37]">
              — Still have a question? —
            </span>
            <h3 className="mt-4 font-display text-3xl md:text-4xl">
              Write to the studio.
            </h3>
            <p className="mt-4 text-[#a3a3a3] font-light max-w-md mx-auto">
              Press, partnerships, preorder support, wholesale. Every note
              reaches us.
            </p>
            <Link
              to="/contact"
              data-testid="faq-contact-cta"
              className="btn-gold mt-8 inline-flex"
            >
              Contact AFRQA™
              <span aria-hidden>→</span>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
